// src/components/BesoinEditor.js
import React, { useState, useEffect, useRef } from "react";
import { Form, Modal } from "react-bootstrap";
import { StarFill, Star, XCircle } from "react-bootstrap-icons";
import { useTranslation } from 'react-i18next';
import { STATES_IDS, PERIOD_IDS, STATUS_IDS, DEFAULT_STATUS, STATE_COLORS, getStateKey } from "../constants/besoinConstants";
import { useAuth } from "../services/AuthContext";
import "../css/Besoin.css";

const BesoinEditor = ({
  onSave,
  onCancel,
  scanResultId = null,
  existingBesoin = null,
  onStatusModalOpen = null,
  onStatusModalClose = null,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const { userGender } = useAuth();
  const textareaRef = useRef(null);

  // Le statut n'a de sens qu'en édition. En création, il est demandé
  // via une modale dédiée juste avant l'enregistrement.
  const isEditing = !!existingBesoin;

  const [text, setText] = useState(existingBesoin ? existingBesoin.text : "");
  const [keywordInput, setKeywordInput] = useState("");
  const [keywords, setKeywords] = useState(existingBesoin ? existingBesoin.keywords || [] : []);
  const [states, setStates] = useState(existingBesoin ? existingBesoin.states || [] : []);
  const [count, setCount] = useState(existingBesoin ? existingBesoin.frequency?.count : 1);
  const [period, setPeriod] = useState(existingBesoin ? existingBesoin.frequency?.period : 2);
  const [pride, setPride] = useState(existingBesoin ? existingBesoin.pride : 3);
  const [status, setStatus] = useState(existingBesoin ? existingBesoin.status : DEFAULT_STATUS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showStatusModal, setShowStatusModal] = useState(false);

  // --- Ouvre/ferme la modale de statut en notifiant le parent (QuickBesoinModal)
  //     afin qu'il puisse masquer/réafficher la 1ère modale en conséquence ---
  const openStatusModal = () => {
    setShowStatusModal(true);
    onStatusModalOpen?.();
  };

  const closeStatusModal = () => {
    setShowStatusModal(false);
    onStatusModalClose?.();
  };

  useEffect(() => {
    if (textareaRef.current) textareaRef.current.focus();
  }, []);

  useEffect(() => {
    if (existingBesoin) {
      setText(existingBesoin.text || "");
      setKeywords(existingBesoin.keywords || []);
      setStates(existingBesoin.states || []);
      setCount(existingBesoin.frequency?.count ?? 1);
      setPeriod(existingBesoin.frequency?.period ?? 2);
      setPride(existingBesoin.pride ?? 3);
      setStatus(existingBesoin.status ?? DEFAULT_STATUS);
    } else {
      setText("");
      setKeywords([]);
      setStates([]);
      setCount(1);
      setPeriod(2);
      setPride(3);
      setStatus(DEFAULT_STATUS);
    }
    setError(null);
    setFieldErrors({});
  }, [existingBesoin]);

  // --- Efface au fil de la saisie le cadre rouge des champs corrigés ---
  useEffect(() => {
    setFieldErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      if (next.text && text.trim()) delete next.text;
      if (next.keywords && keywords.length > 0) delete next.keywords;
      if (next.states && states.length > 0) delete next.states;
      if (next.frequency && count && count >= 1) delete next.frequency;
      return next;
    });
  }, [text, keywords, states, count]);

  // --- Gestion des mots-clés (ajout via Entrée ou virgule) ---
  const addKeyword = (raw) => {
    const value = raw.trim();
    if (!value) return;
    if (!keywords.includes(value)) {
      setKeywords((prev) => [...prev, value]);
    }
    setKeywordInput("");
  };

  const handleKeywordKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addKeyword(keywordInput);
    } else if (e.key === "Backspace" && !keywordInput && keywords.length > 0) {
      setKeywords((prev) => prev.slice(0, -1));
    }
  };

  const removeKeyword = (kw) => {
    setKeywords((prev) => prev.filter((k) => k !== kw));
  };

  // --- Gestion des états (multi-sélection) ---
  const toggleState = (id) => {
    setStates((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  // --- Validation du formulaire, commune aux deux modes ---
  //     Ne renseigne plus de message d'erreur : elle marque les champs
  //     concernés dans fieldErrors, ce qui déclenche leur cadre rouge.
  const validate = () => {
    const errors = {};
    if (!text.trim()) errors.text = true;
    if (keywords.length === 0) errors.keywords = true;
    if (states.length === 0) errors.states = true;
    if (!count || count < 1) errors.frequency = true;

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // --- Sauvegarde effective (appelée directement en édition, ou après
  //     confirmation du statut dans la modale en création) ---
  const performSave = async () => {
    setLoading(true);
    try {
      const besoin = {
        text: text.trim(),
        keywords,
        states,
        frequency: { count: Number(count), period: Number(period) },
        pride: Number(pride),
        status: Number(status),
        scanResultId: scanResultId || null,
      };

      await onSave(besoin);
      closeStatusModal();
    } catch (err) {
      console.error("Erreur :", err);
      setError(err.message || t('besoin.input.error_generic'));
    } finally {
      setLoading(false);
    }
  };

  // --- Clic sur "Enregistrer" ---
  const handleSaveClick = () => {
    setError(null);
    if (!validate()) return;

    if (isEditing) {
      // En édition, le statut est déjà visible dans le formulaire : on enregistre directement.
      performSave();
    } else {
      // En création, on demande le statut via une modale avant d'enregistrer.
      openStatusModal();
    }
  };

  return (
    <div className={`besoin-editor ${showStatusModal ? "besoin-editor-dimmed" : ""}`}>
      {/* --- Texte du besoin --- */}
      <label>{t('besoin.input.text')}</label>
      <textarea
        ref={textareaRef}
        className={fieldErrors.text ? "besoin-field-invalid" : ""}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t('besoin.input.text_placeholder')}
        rows={2}
        disabled={readOnly}
      />

      {/* --- Mots-clés --- */}
      <label>{t('besoin.input.keywords')}</label>
      <div className={`besoin-keywords-input ${fieldErrors.keywords ? "besoin-field-invalid" : ""} ${readOnly ? "readonly" : ""}`}>
        {keywords.map((kw) => (
          <span key={kw} className="besoin-keyword-chip">
            {kw}
            {!readOnly && <XCircle size={14} onClick={() => removeKeyword(kw)} style={{ cursor: "pointer", marginLeft: "4px" }} />}
          </span>
        ))}
        {!readOnly && (
          <input
            type="text"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyDown={handleKeywordKeyDown}
            onBlur={() => addKeyword(keywordInput)}
            placeholder={t('besoin.input.keywords_placeholder')}
          />
        )}
      </div>

      {/* --- États --- */}
      <label>{t('besoin.input.states')}</label>
      <div className={`besoin-states-select ${fieldErrors.states ? "besoin-field-invalid" : ""}`}>
        {STATES_IDS.map((id) => (
          <button
            type="button"
            key={id}
            className={`besoin-state-pill ${states.includes(id) ? "active" : ""}`}
            style={{ backgroundColor: STATE_COLORS[id], borderColor: STATE_COLORS[id], cursor: readOnly ? "default" : "pointer", opacity: readOnly && !states.includes(id) ? 0.5 : 1 }}
            onClick={() => !readOnly && toggleState(id)}
            disabled={readOnly}
          >
            {t(getStateKey(id, userGender))}
          </button>
        ))}
      </div>

      {/* --- Fréquence --- */}
      <label>{t('besoin.input.frequency')}</label>
      <div className="besoin-frequency-row">
        <Form.Control
          type="number"
          min={1}
          value={count}
          onChange={(e) => setCount(e.target.value)}
          className={fieldErrors.frequency ? "besoin-field-invalid" : ""}
          style={{ width: "80px" }}
          disabled={readOnly}
        />
        <Form.Select value={period} onChange={(e) => setPeriod(e.target.value)} style={{ width: "160px" }} disabled={readOnly}>
          {PERIOD_IDS.map((id) => (
            <option key={id} value={id}>
              {t(`besoin.periods_${id}`)}
            </option>
          ))}
        </Form.Select>
      </div>

      {/* --- Fierté --- */}
      <label>{t('besoin.input.pride')}</label>
      <div className="besoin-pride-select">
        {[1, 2, 3, 4, 5].map((n) => (
          <span key={n} onClick={() => !readOnly && setPride(n)} style={{ cursor: readOnly ? "default" : "pointer" }}>
            {n <= pride ? <StarFill size={22} color="#e8c25a" /> : <Star size={22} color="#666" />}
          </span>
        ))}
      </div>

      {/* --- Statut (uniquement en édition, visible directement dans le formulaire) --- */}
      {(isEditing || readOnly) && (
        <>
          <label>{t('besoin.status')}</label>
          <Form.Select value={status} onChange={(e) => setStatus(e.target.value)} disabled={readOnly}>
            {STATUS_IDS.map((id) => (
              <option key={id} value={id}>
                {t(`besoin.status_${id}`)}
              </option>
            ))}
          </Form.Select>
        </>
      )}

      {error && <p className="besoin-error">{error}</p>}

      <div className="besoin-actions">
        {!readOnly ? (
          <>
            <button className="besoin-btn save-btn" onClick={handleSaveClick} disabled={loading}>
              {loading ? t('besoin.input.saving') : existingBesoin ? t('besoin.input.update') : t('besoin.input.save')}
            </button>
            {onCancel && (
              <button className="besoin-btn cancel-btn" onClick={onCancel} disabled={loading}>
                {t('besoin.input.cancel')}
              </button>
            )}
          </>
        ) : (
          <button className="besoin-btn cancel-btn" onClick={onCancel}>
            {t('besoin.input.cancel', { defaultValue: 'Fermer' })}
          </button>
        )}
      </div>

      {/* --- Modale de statut (uniquement en création), affichée juste avant l'enregistrement --- */}
      {!isEditing && (
        <Modal
          show={showStatusModal}
          onHide={closeStatusModal}
          centered
          contentClassName="besoin-modal-content"
        >
          <Modal.Header className="besoin-modal-header">
            <button
              type="button"
              className="besoin-modal-close"
              onClick={closeStatusModal}
              aria-label={t('besoin.input.cancel')}
            >
              &times;
            </button>

            <Modal.Title>{t('besoin.status')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_IDS.map((id) => (
                <option key={id} value={id}>
                  {t(`besoin.status_${id}`)}
                </option>
              ))}
            </Form.Select>
          </Modal.Body>
          <Modal.Footer>           
            <button className="besoin-btn save-btn" onClick={performSave} disabled={loading}>
              {loading ? t('besoin.input.saving') : t('besoin.input.save')}
            </button>
            <button className="besoin-btn cancel-btn" onClick={closeStatusModal} disabled={loading}>
              {t('besoin.input.cancel')}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default BesoinEditor;